import { useEffect, useMemo } from 'react'
import type { MouseEvent, ReactElement } from 'react'
import { Button, Flex, Text } from '@buffer-mono/popcorn'

import { useUser } from '../../../../common/context/User'
import { MODALS } from '../../../../common/types'

import {
  trackModalDismissed,
  trackUpgradePathViewed,
} from '../../../../common/hooks/useSegmentTracking'
import useStartTrial from '../../../../common/hooks/useStartTrial'
import { Error } from '../../../../common/components/Error'
import UpgradePathsTracking from '../../../../tracking/UpgradePaths'
import { prefixWithProduct } from '../../../../common/utils/prefixWithProduct'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'
import { getPublishUrl } from '@buffer-mono/shared-utils/urls'

import styles from './PaywallCTAs.module.css'

type CtaProp = {
  feature: string
  paywallType: MODALS.featurePaywall | MODALS.analyzeUpgradePath
}

export const PaywallCtas = ({
  feature,
  paywallType,
}: CtaProp): ReactElement => {
  const user = useUser()
  const { openModal, dismissModal } = useModalManager()
  const canStartTrial = user?.currentOrganization?.billing?.canStartTrial

  const ctaAttributes = useMemo(() => {
    const ctaButton = canStartTrial ? 'startTrial' : 'upgradePlan'
    const ctaView = 'paywallModal'
    const ctaLocation = 'paywallModal'

    return {
      ctaView,
      ctaLocation,
      cta: `${ctaView}-${ctaLocation}-${ctaButton}-1`,
      upgradePathName: canStartTrial
        ? `${feature}-trial`
        : `${feature}-upgrade`,
      ctaButton,
    }
  }, [canStartTrial, feature])

  const { startTrial, trial, error, processing } = useStartTrial({
    user,
    attribution: {
      cta: prefixWithProduct(ctaAttributes.cta),
      upgradePathName:
        ctaAttributes.upgradePathName /* This attribution are sent in the Start Trial event from backend */,
    },
  })
  const { trackCTAClicked } = UpgradePathsTracking(user)

  useEffect(() => {
    trackUpgradePathViewed({
      payload: {
        upgradePathName: ctaAttributes.upgradePathName,
        cta: ctaAttributes.cta,
      },
      user,
    })
  }, [user, ctaAttributes.upgradePathName, ctaAttributes.cta])

  useEffect(() => {
    if (trial?.__typename === 'StartTrialResponse') {
      /* close first this modal */
      dismissModal()
      openModal({
        key: MODALS.success,
        data: {
          startedTrial: true,
        },
      })
    }
  }, [openModal, trial])

  const handleMainCTAClick = (): void => {
    /* Track CTA Clicked event */
    trackCTAClicked({
      ctaView: ctaAttributes.ctaView,
      ctaLocation: ctaAttributes.ctaLocation,
      ctaButton: ctaAttributes.ctaButton,
      upgradePathName: ctaAttributes.upgradePathName,
    })
    /*
     * If the user can start a trial, the trial will start inmediately
     * In other case, the Pricing Modal will be opened to upgrade the subscription
     */
    if (canStartTrial) {
      startTrial()
    } else {
      /* close first this modal */
      dismissModal()
      openModal({
        key: MODALS.planSelector,
        data: {
          cta: ctaAttributes.cta,
          source: paywallType,
          upgradePathName: ctaAttributes.upgradePathName,
        },
      })
    }
  }

  const handleNoThanksClick = (event: MouseEvent<HTMLButtonElement>): void => {
    dismissModal()
    trackModalDismissed({
      payload: {
        modalName: paywallType,
        method: 'noThanks',
      },
      user,
    })
    // If the paywall type is analyze upgrade path, we don't want to redirect to the publish page
    // because we most likely will be in the publish product page already
    if (paywallType === MODALS.analyzeUpgradePath) {
      event.preventDefault()
    }
  }

  const getButtonLabel = (): string => {
    const label = canStartTrial ? 'Try it free for 14 days' : 'Upgrade Plan'
    if (canStartTrial && processing) {
      return 'Processing ...'
    }
    return label
  }

  return (
    <Flex direction="column" gap="sm">
      <Flex direction="row" gap="xs" align="center">
        <Button
          variant="primary"
          size="large"
          data-testid={`${feature}-paywall-cta-button`}
          disabled={canStartTrial && processing}
          onClick={handleMainCTAClick}
        >
          {getButtonLabel()}
        </Button>
        <Button
          as="a"
          variant="tertiary"
          size="large"
          href={getPublishUrl()}
          onClick={handleNoThanksClick}
        >
          No, Thanks
        </Button>
      </Flex>
      {error && <Error isInline={false} error={error} />}
      {canStartTrial && (
        <Text size="sm" color="subtle" className={styles.startTrialInfo}>
          No credit card required
        </Text>
      )}
    </Flex>
  )
}
